import { useEffect, useMemo, useState } from "react";
import { Copy, Download, Image as ImageIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { SectionHeading } from "@/components/SectionHeading";
import { formatDate, slugify } from "@/utils/format";

export function SocialPostGenerator({ chronicles = [] }) {
  const [selectedId, setSelectedId] = useState("");
  const selected = chronicles.find((item) => item.id === selectedId) || chronicles[0];
  const [winner, setWinner] = useState("");
  const [score, setScore] = useState("");
  const [quote, setQuote] = useState("");

  useEffect(() => {
    if (selected) {
      setWinner(selected.winner);
      setScore(String(selected.score));
      setQuote("");
    }
  }, [selected?.id]);

  const summaryText = useMemo(() => {
    if (!selected) return "";
    const cleanQuote = quote ? ` "${quote}"` : "";
    return `${selected.title}\n${winner} gana en ${selected.location} con ${score} platos.${cleanQuote}\nCrónica completa, fotografías y clasificaciones disponibles en la web.\n#TiroAlPlato #Resultados #${slugify(selected.location).replaceAll("-", "")}`;
  }, [selected, winner, score, quote]);

  const copySummary = async () => {
    await navigator.clipboard.writeText(summaryText);
    toast.success("Resumen copiado para redes o prensa");
  };

  const downloadCard = () => {
    if (!selected) return;
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1350;
    const context = canvas.getContext("2d");
    const render = (image) => {
      drawCard(context, canvas, selected, winner, score, image);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${slugify(selected.title)}-resumen.png`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success("Tarjeta social descargada");
      }, "image/png");
    };
    if (!selected.image) {
      render(null);
      return;
    }
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => render(image);
    image.onerror = () => render(null);
    image.src = selected.image;
  };

  return (
    <section id="redes" className="section-shell" data-testid="social-generator-section">
      <SectionHeading number="05" eyebrow="Redes, prensa y Flickr" title="Del podio a la publicación" description="Elige una crónica, ajusta ganador y puntuación, copia el texto editorial o descarga una tarjeta vertical lista para compartir." />

      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="poster-card p-6 sm:p-8" data-testid="social-generator-form">
          <label className="field-label" htmlFor="social-chronicle-select">Crónica base</label>
          <Select value={selected?.id || ""} onValueChange={setSelectedId}>
            <SelectTrigger id="social-chronicle-select" className="h-13 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid="social-chronicle-select">
              <SelectValue placeholder="Selecciona una crónica" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-panel text-slate-100">
              {chronicles.map((chronicle) => (
                <SelectItem key={chronicle.id} value={chronicle.id}>{chronicle.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="field-label">Ganador/a</span>
              <Input value={winner} onChange={(event) => setWinner(event.target.value)} className="h-13 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid="social-winner-input" />
            </label>
            <label className="block">
              <span className="field-label">Platos</span>
              <Input value={score} onChange={(event) => setScore(event.target.value)} type="number" min="0" className="h-13 rounded-none border-white/10 bg-charcoal text-slate-100" data-testid="social-score-input" />
            </label>
          </div>

          <label className="mt-6 block">
            <span className="field-label">Frase destacada opcional</span>
            <Textarea value={quote} onChange={(event) => setQuote(event.target.value)} placeholder="Una declaración breve del ganador o del club" className="min-h-28 rounded-none border-white/10 bg-charcoal text-slate-100 placeholder:text-slate-600" data-testid="social-quote-textarea" />
          </label>

          <div className="mt-6 border border-white/10 bg-white/[0.03] p-5">
            <p className="eyebrow">Texto preparado</p>
            <p className="mt-4 whitespace-pre-line text-sm leading-7 text-slate-300" data-testid="social-summary-text">{summaryText}</p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button type="button" onClick={copySummary} className="rounded-none bg-clay font-mono text-xs uppercase tracking-[0.2em] text-charcoal hover:bg-brass" data-testid="social-copy-button">
              Copiar texto <Copy className="h-4 w-4" />
            </Button>
            <Button type="button" onClick={downloadCard} variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-xs uppercase tracking-[0.2em] text-slate-100 hover:border-clay/60 hover:bg-clay/10 hover:text-clay" data-testid="social-download-button">
              Descargar tarjeta <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {selected ? (
          <div className="social-preview" data-testid="social-preview-card">
            <img src={selected.image} alt={`Vista previa social de ${selected.title}`} className="absolute inset-0 h-full w-full object-cover image-treatment" />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/35 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 sm:p-10">
              <div className="mb-5 inline-flex items-center gap-3 border border-clay/40 bg-charcoal/75 px-4 py-2 backdrop-blur">
                <ImageIcon className="h-4 w-4 text-clay" />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-clay">Resumen oficial</span>
              </div>
              <h3 className="font-display text-5xl uppercase leading-[0.86] text-slate-50 sm:text-7xl">{selected.title}</h3>
              <div className="mt-6 flex items-end justify-between gap-6 border-t border-white/20 pt-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">Ganador/a</p>
                  <p className="mt-1 text-2xl font-bold text-slate-50" data-testid="social-preview-winner">{winner}</p>
                  <p className="mt-1 text-sm text-slate-400">{formatDate(selected.date)} · {selected.location}</p>
                </div>
                <div className="text-right">
                  <span className="font-display text-7xl leading-none text-clay" data-testid="social-preview-score">{score}</span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">platos</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function drawCard(context, canvas, chronicle, winner, score, image) {
  context.fillStyle = "#0D0F12";
  context.fillRect(0, 0, canvas.width, canvas.height);
  if (image) {
    const scale = Math.max(canvas.width / image.width, canvas.height / image.height);
    const width = image.width * scale;
    const height = image.height * scale;
    context.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
  }
  const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, "rgba(13,15,18,0.08)");
  gradient.addColorStop(0.58, "rgba(13,15,18,0.62)");
  gradient.addColorStop(1, "rgba(13,15,18,0.97)");
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "#FF6B00";
  context.lineWidth = 10;
  context.strokeRect(42, 42, canvas.width - 84, canvas.height - 84);
  context.fillStyle = "#FF6B00";
  context.font = "700 28px JetBrains Mono, monospace";
  context.fillText("TODOTIRO AL PLATO · RESUMEN OFICIAL", 82, 132);
  context.fillStyle = "#F8FAFC";
  context.font = "86px Bebas Neue, sans-serif";
  wrapCanvasText(context, chronicle.title.toUpperCase(), 82, 880, 920, 86);
  context.font = "700 34px Manrope, sans-serif";
  context.fillText(winner, 82, 1110);
  context.fillStyle = "#FF6B00";
  context.font = "150px Bebas Neue, sans-serif";
  context.fillText(String(score), 82, 1250);
  context.fillStyle = "#94A3B8";
  context.font = "700 22px JetBrains Mono, monospace";
  context.fillText(`${formatDate(chronicle.date).toUpperCase()} · ${chronicle.location.toUpperCase()}`, 82, 1290);
}

function wrapCanvasText(context, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  words.forEach((word, index) => {
    const testLine = `${line}${word} `;
    if (context.measureText(testLine).width > maxWidth && index > 0) {
      context.fillText(line, x, y);
      line = `${word} `;
      y += lineHeight;
    } else {
      line = testLine;
    }
  });
  context.fillText(line, x, y);
}
